import { Users } from "lucide-react";
import React, { useMemo } from "react";
import { Alert, Loader, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import useSegmentPreviewApiService from "../services/api/SegmentPreviewApiService";
import SegmentRule from "../services/model/SegmentRule";
import { areRulesValid } from "../utils/ruleValidation";
import { SEGMENT_PREVIEW_COLORS } from "../constants/segmentConstants";

type RulePreviewProps = {
  rules: SegmentRule[];
  logicOperator: "AND" | "OR";
  employerId: number;
};

const RulePreview: React.FC<RulePreviewProps> = ({
  rules,
  logicOperator,
  employerId,
}) => {
  // Debounce both rules AND logicOperator together
  const [debouncedRules] = useDebouncedValue(rules, 500);
  const [debouncedLogicOperator] = useDebouncedValue(logicOperator, 500);

  const isValidRules = useMemo(
    () => areRulesValid(debouncedRules),
    [debouncedRules]
  );

  // Create stable request object
  const request = useMemo(
    () => ({
      employer: employerId,
      rules: debouncedRules,
      logic_operator: debouncedLogicOperator,
    }),
    [employerId, debouncedRules, debouncedLogicOperator]
  );

  const { data, isLoading, isError } = useSegmentPreviewApiService(
    request,
    isValidRules
  );

  if (!isValidRules) {
    return (
      <Alert
        icon={<Users size={16} />}
        color="gray"
        variant="light"
        styles={{
          root: {
            backgroundColor: "#f8f9fa",
          },
        }}
      >
        <Text size="sm" color="dimmed">
          Complete the rules to see estimated match count
        </Text>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Alert
        icon={<Loader size={16} />}
        color="orange"
        variant="light"
        styles={{
          root: {
            backgroundColor: SEGMENT_PREVIEW_COLORS.background,
          },
        }}
      >
        <Text size="sm" weight={500} color={SEGMENT_PREVIEW_COLORS.text}>
          Calculating estimated match...
        </Text>
      </Alert>
    );
  }

  if (isError) {
    return (
      <Alert
        icon={<Users size={16} />}
        color="red"
        variant="light"
        styles={{
          root: {
            backgroundColor: "rgba(255, 107, 107, 0.1)",
          },
        }}
      >
        <Text size="sm" color="red">
          Unable to calculate preview
        </Text>
      </Alert>
    );
  }

  return (
    <Alert
      icon={<Users size={16} />}
      color="orange"
      variant="light"
      styles={{
        root: {
          backgroundColor: SEGMENT_PREVIEW_COLORS.background,
        },
      }}
    >
      <Text size="sm" weight={500} color={SEGMENT_PREVIEW_COLORS.text}>
        Estimated match: {data?.matched_count || 0} Employees
      </Text>
    </Alert>
  );
};

export default RulePreview;
